import { api, type RaceSummary, type ScheduleData } from "@/api";
import { scheduleWindowAt } from "@/app/prefetch";

export const SCHEDULE_LIMIT = 2000;
export const MAX_WINDOW_MS = 48 * 60 * 60_000;
export const RESULTS_PAGE_SIZE = 20;
export type ScheduleWindow = ReturnType<typeof scheduleWindowAt>;
export type ScheduleView = "upcoming" | "results";
export type ScheduleState = {
  view: ScheduleView;
  window: ScheduleWindow;
  follow: boolean;
  league: string;
  distance: string;
};
export type ResultSummary = Awaited<
  ReturnType<typeof api.results>
>["races"][number];

export function readScheduleState(
  search: string,
  fallback: ScheduleWindow,
): ScheduleState {
  const params = new URLSearchParams(search);
  const from = Date.parse(params.get("from") ?? "");
  const to = Date.parse(params.get("to") ?? "");
  const valid = Number.isFinite(from) && Number.isFinite(to) && to > from;
  const follow = params.has("follow") ? params.get("follow") === "1" : !valid;
  return {
    view: params.get("view") === "results" ? "results" : "upcoming",
    follow,
    window:
      valid && !follow
        ? {
            from: new Date(from).toISOString(),
            to: new Date(Math.min(to, from + MAX_WINDOW_MS)).toISOString(),
          }
        : fallback,
    league: params.get("league") ?? "",
    distance: /^\d+$/.test(params.get("distance") ?? "")
      ? params.get("distance")!
      : "",
  };
}

export function scheduleSearch(
  search: string,
  state: ScheduleState,
  browserSearch = "",
) {
  // Preserve unrelated session/recovery parameters without reading or consuming them.
  const params = new URLSearchParams(browserSearch);
  new URLSearchParams(search).forEach((value, key) => params.set(key, value));
  params.set("view", state.view);
  params.set("follow", state.follow ? "1" : "0");
  params.set("from", state.window.from);
  params.set("to", state.window.to);
  if (state.league) params.set("league", state.league);
  else params.delete("league");
  if (state.distance) params.set("distance", state.distance);
  else params.delete("distance");
  return params.toString();
}

export const scheduleKey = (window: ScheduleWindow) =>
  ["schedule", window] as const;

export function adjacentWindow(
  window: ScheduleWindow,
  direction: -1 | 1,
): ScheduleWindow {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  const offset = (to - from) * direction;
  return {
    from: new Date(from + offset).toISOString(),
    to: new Date(to + offset).toISOString(),
  };
}

export function narrowerWindow(window: ScheduleWindow): ScheduleWindow {
  const from = Date.parse(window.from);
  const duration = Math.max(1, Math.floor((Date.parse(window.to) - from) / 2));
  return { from: window.from, to: new Date(from + duration).toISOString() };
}

export function upcomingInWindow(
  races: RaceSummary[],
  window: ScheduleWindow,
  league = "",
  distance = "",
) {
  return calendarInWindow(races, window, league, distance).filter(
    (race) =>
      !["official", "abandoned", "voided", "cancelled"].includes(race.status),
  );
}

export function calendarInWindow(
  races: RaceSummary[],
  window: ScheduleWindow,
  league = "",
  distance = "",
) {
  const from = Date.parse(window.from);
  const to = Date.parse(window.to);
  return races
    .filter((race) => {
      const at = Date.parse(race.scheduledAt);
      return (
        at >= from &&
        at < to &&
        (!league || race.track === league) &&
        (!distance || race.distanceM === Number(distance))
      );
    })
    .sort(
      (a, b) =>
        Date.parse(a.scheduledAt) - Date.parse(b.scheduledAt) ||
        a.raceId.localeCompare(b.raceId),
    );
}

export function scheduleOptions(data: ScheduleData | undefined) {
  return {
    leagues: data?.tracks ?? [],
    distances: [
      ...new Set(data?.races.map((race) => race.distanceM) ?? []),
    ].sort((a, b) => a - b),
  };
}

export function newestResults(
  pages: Array<{ races: ResultSummary[] }>,
): ResultSummary[] {
  const seen = new Set<string>();
  return pages
    .flatMap((page) => page.races)
    .filter((race) => {
      if (seen.has(race.raceId)) return false;
      seen.add(race.raceId);
      return true;
    })
    .sort((a, b) => {
      const left = a.finishedAt ? Date.parse(a.finishedAt) : 0;
      const right = b.finishedAt ? Date.parse(b.finishedAt) : 0;
      return right - left;
    });
}

export function localDay(at: string | null) {
  return at
    ? new Date(at).toLocaleDateString([], {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Finish date unavailable";
}

export function localTime(at: string) {
  return new Date(at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function localDateTime(at: string) {
  return new Date(at).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
